/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
    Modal, Card, Select, Input, DatePicker, Button, Tag, message,
    Collapse, Divider, Tooltip, Alert, Spin
} from 'antd';
import {
    FileExcelOutlined, UploadOutlined, LoadingOutlined, SaveOutlined, WarningOutlined, HistoryOutlined
} from '@ant-design/icons';
import moment from 'moment';
import QoCService from '../../services/QoCService';
import {
    exportCapCommitmentExcel, importCapCommitmentExcel, ROOT_CAUSES
} from '../../utils/qocCapCommitmentExcel';

const { TextArea } = Input;
const { Panel } = Collapse;

const STATUS_CFG = {
    open:        { color:'#1890ff', label:'Open' },
    in_progress: { color:'#fa8c16', label:'In Progress' },
    overdue:     { color:'#f5222d', label:'Overdue' },
    closed:      { color:'#52c41a', label:'Closed' },
    cancelled:   { color:'#999',    label:'Cancelled' },
};

function FindingRow({ row, onChange, isNew }) {
    return (
        <div style={{ padding:'12px 14px', marginBottom:10, borderRadius:8, border:'1px solid #f0f0f0',
            background: isNew ? '#fffbf5' : '#fff' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                <div>
                    <Tag color="blue" style={{ fontFamily:'monospace' }}>{row.ref_no}</Tag>
                    {row.clause_code && <Tag>{row.clause_code}</Tag>}
                    {row.fulfillment && (
                        <Tag color={row.fulfillment === 'Not Met' ? 'red' : 'orange'}>{row.fulfillment}</Tag>
                    )}
                    {isNew && <Tag color="gold" icon={<HistoryOutlined/>}>New</Tag>}
                </div>
                <Select size="small" style={{ width:130 }} value={row.status || 'open'}
                    onChange={v => onChange({ ...row, status: v })}>
                    {Object.entries(STATUS_CFG).filter(([k]) => k !== 'overdue').map(([k, v]) => (
                        <Select.Option key={k} value={k}>{v.label}</Select.Option>
                    ))}
                </Select>
            </div>

            {row.checklist_gaps?.length > 0 && (
                <Alert type="warning" showIcon icon={<WarningOutlined/>} style={{ marginBottom:8 }}
                    message={<span style={{ fontSize:12, fontWeight:700 }}>
                        Missing evidence ({row.checklist_checked}/{row.checklist_total} checklist items verified)
                    </span>}
                    description={
                        <ul style={{ margin:'4px 0 0', paddingLeft:18, fontSize:12 }}>
                            {row.checklist_gaps.map((g, i) => <li key={i}>{g}</li>)}
                        </ul>
                    }/>
            )}

            <div style={{ marginBottom:8 }}>
                <label style={{ fontSize:11, fontWeight:700, color:'#888' }}>FINDING</label>
                <TextArea rows={2} value={row.finding || ''}
                    onChange={e => onChange({ ...row, finding: e.target.value })}/>
            </div>

            <div style={{ display:'flex', gap:10, marginBottom:8 }}>
                <div style={{ flex:1 }}>
                    <label style={{ fontSize:11, fontWeight:700, color:'#888' }}>ROOT CAUSE(S)</label>
                    <Select mode="multiple" style={{ width:'100%' }} size="small"
                        value={row.root_cause ? row.root_cause.split(', ').filter(Boolean) : []}
                        onChange={vals => onChange({ ...row, root_cause: vals.join(', ') })}
                        placeholder="Select one or more...">
                        {ROOT_CAUSES.map(rc => <Select.Option key={rc} value={rc}>{rc}</Select.Option>)}
                    </Select>
                </div>
            </div>

            <div style={{ marginBottom:8 }}>
                <label style={{ fontSize:11, fontWeight:700, color:'#888' }}>CORRECTIVE ACTION</label>
                <TextArea rows={2} value={row.action_description || ''}
                    onChange={e => onChange({ ...row, action_description: e.target.value })}/>
            </div>

            <div style={{ display:'flex', gap:10 }}>
                <div style={{ flex:1 }}>
                    <label style={{ fontSize:11, fontWeight:700, color:'#888' }}>RESPONSIBLE PERSON</label>
                    <Input size="small" value={row.responsible_person || ''}
                        onChange={e => onChange({ ...row, responsible_person: e.target.value })}/>
                </div>
                <div style={{ width:160 }}>
                    <label style={{ fontSize:11, fontWeight:700, color:'#888' }}>TARGET DATE</label>
                    <DatePicker size="small" style={{ width:'100%' }}
                        value={row.target_date ? moment(row.target_date) : null}
                        onChange={v => onChange({ ...row, target_date: v ? v.format('YYYY-MM-DD') : null })}/>
                </div>
            </div>
        </div>
    );
}

export default function CapCommitmentModal({ facility, onClose, onSaved }) {
    const [loading, setLoading]     = useState(true);
    const [rows, setRows]           = useState([]); // unified: new findings + open items, each carries period_label
    const [saving, setSaving]       = useState(false);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const importInputRef = useRef(null);

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await QoCService.getCapFacilityPreview(facility.id);
            const data = res.data?.data || { new_findings:[], open_items:[] };

            const newRows = (data.new_findings || []).map(f => ({
                ...f,
                cap_id: null,
                status: 'open',
                root_cause: '',
                action_description: '',
                responsible_person: '',
                target_date: null,
                _isNew: true,
            }));
            const openRows = (data.open_items || []).map(o => ({
                ...o,
                cap_id: o.id,
                _isNew: false,
            }));

            setRows([...newRows, ...openRows]);
        } catch (e) {
            message.error('Failed to load CAP context: ' + (e.response?.data?.message || e.message));
        } finally { setLoading(false); }
    };

    const updateRow = (index, updated) => {
        setRows(prev => prev.map((r, i) => i === index ? updated : r));
    };

    // ── Group everything by period_label, newest period first ─────────────
    const periods = [...new Set(rows.map(r => r.period_label || 'No Period'))]
        .sort((a, b) => b.localeCompare(a));
    const rowsByPeriod = periods.reduce((acc, p) => {
        acc[p] = rows.filter(r => (r.period_label || 'No Period') === p);
        return acc;
    }, {});

    const handleSaveAll = async () => {
        setSaving(true);
        let ok = 0, failed = 0;
        try {
            for (const row of rows) {
                // Skip untouched new rows — don't create empty CAP items
                // just because the popup was opened.
                if (row._isNew && !row.finding && !row.action_description && !row.root_cause) continue;

                const payload = {
                    ...(row.cap_id ? { id: row.cap_id } : {}),
                    assessment_id: row.assessment_id || null,
                    facility_id: facility.id,
                    clause_id: row.clause_id || null,
                    me_id: row.me_id || null,
                    ref_no: row.ref_no || null,
                    period_label: row.period_label || null,
                    finding: row.finding || '',
                    fulfillment: row.fulfillment || null,
                    root_cause: row.root_cause || '',
                    action_description: row.action_description || '',
                    responsible_person: row.responsible_person || '',
                    target_date: row.target_date || null,
                    status: row.status || 'open',
                };
                try {
                    await QoCService.saveCapItem(payload);
                    ok++;
                } catch (e) { failed++; }
            }
            message.success(`Saved ${ok} CAP item${ok !== 1 ? 's' : ''}${failed ? `, ${failed} failed` : ''}`);
            onSaved && onSaved();
            if (!failed) onClose();
        } finally { setSaving(false); }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const exportRows = rows.map(r => ({
                cap_id: r.cap_id,
                me_id: r.me_id,
                ref_no: r.ref_no,
                clause_code: r.clause_code,
                finding: r.finding,
                checklist_gaps: r.checklist_gaps,
                root_cause_keys: [],
                action_description: r.action_description,
                responsible_person: r.responsible_person,
                target_date: r.target_date,
                status: r.status,
            }));
            await exportCapCommitmentExcel({ facility, rows: exportRows });
            message.success('Excel downloaded — complete with the facility and import back when ready.');
        } catch (e) { message.error('Export failed: ' + e.message); }
        finally { setExporting(false); }
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        setImporting(true);
        try {
            const { rows: imported, errors } = await importCapCommitmentExcel(file);
            if (errors.length) {
                Modal.warning({
                    title: `Imported with ${errors.length} issue(s)`,
                    content: <div>{errors.map((e, i) => <div key={i} style={{ color:'#f5222d', fontSize:12 }}>⚠ {e}</div>)}</div>,
                });
            }
            setRows(prev => prev.map(r => {
                const match = imported.find(im =>
                    (r.cap_id && im.cap_id && String(im.cap_id) === String(r.cap_id)) ||
                    (r.me_id && im.me_id && String(im.me_id) === String(r.me_id))
                );
                if (!match) return r;
                return {
                    ...r,
                    root_cause: match.root_cause || r.root_cause,
                    action_description: match.action_description || r.action_description,
                    responsible_person: match.responsible_person || r.responsible_person,
                    target_date: match.target_date || r.target_date,
                    status: match.status || r.status,
                    finding: match.finding || r.finding,
                };
            }));
            message.success(`Imported ${imported.length} row(s) from Excel ✓`);
        } catch (err) {
            message.error('Import failed: ' + err.message);
        } finally { setImporting(false); }
    };

    return (
        <Modal
            open
            onCancel={onClose}
            width={820}
            style={{ top:20 }}
            title={
                <div>
                    <div style={{ fontWeight:800, fontSize:15 }}>Corrective Action Plan — {facility.business_name}</div>
                    <div style={{ fontSize:11, color:'#888', fontWeight:400 }}>
                        All open findings across every assessed clause, grouped by period
                    </div>
                </div>
            }
            footer={[
                <Button key="export" icon={<FileExcelOutlined/>} loading={exporting} onClick={handleExport}
                    style={{ borderColor:'#52c41a', color:'#52c41a' }}>
                    Export for Facility
                </Button>,
                <Button key="import" icon={importing ? <LoadingOutlined/> : <UploadOutlined/>} loading={importing}
                    onClick={() => importInputRef.current?.click()}
                    style={{ borderColor:'#1890ff', color:'#1890ff' }}>
                    Import Completed
                </Button>,
                <Button key="cancel" onClick={onClose}>Close</Button>,
                <Button key="save" type="primary" icon={<SaveOutlined/>} loading={saving} onClick={handleSaveAll}>
                    Save Commitments
                </Button>,
            ]}
        >
            <input ref={importInputRef} type="file" accept=".xlsx" style={{ display:'none' }} onChange={handleImport}/>

            {loading ? <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div> : (
                <>
                    {rows.length === 0 && (
                        <Alert type="success" showIcon
                            message="No open findings for this facility right now — nothing Not Met or Partially Met, and no open CAP items."/>
                    )}

                    <Collapse defaultActiveKey={periods.slice(0, 1)} size="small">
                        {periods.map(period => {
                            const periodRows = rowsByPeriod[period];
                            const newCount = periodRows.filter(r => r._isNew).length;
                            const openCount = periodRows.length - newCount;
                            return (
                                <Panel
                                    key={period}
                                    header={
                                        <span>
                                            <strong>{period}</strong>
                                            <span style={{ marginLeft:10, fontSize:11, color:'#888' }}>
                                                {newCount > 0 && <Tag color="gold">{newCount} new</Tag>}
                                                {openCount > 0 && <Tag>{openCount} open</Tag>}
                                            </span>
                                        </span>
                                    }>
                                    {periodRows.map((row) => {
                                        const idx = rows.indexOf(row);
                                        return (
                                            <FindingRow
                                                key={row._isNew ? `new-${row.me_id}-${row.assessment_id}` : `open-${row.cap_id}`}
                                                row={row} isNew={row._isNew}
                                                onChange={updated => updateRow(idx, updated)}/>
                                        );
                                    })}
                                </Panel>
                            );
                        })}
                    </Collapse>
                </>
            )}
        </Modal>
    );
}
